import { baseApi, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { AQSI_TAG_TYPES, AQSI_TAGS } from '../../../shared/api/tags'
import { AQSI_URLS } from '../../../shared/api/urls'
import type {
  AqsiCashiers,
  AqsiDevices,
  AqsiFiscalCheck,
  AqsiFreshness,
  AqsiFreshnessCheck,
  AqsiGoodsPage,
  AqsiProbeStatus,
  AqsiRefreshResult,
  AqsiTaxSummary,
} from '../model/types'
import {
  decodeCashiers,
  decodeDevices,
  decodeFiscalCheck,
  decodeFreshness,
  decodeFreshnessCheck,
  decodeGoodsPage,
  decodeProbeStatus,
  decodeRefresh,
  decodeSetPrice,
  decodeGoodPrices,
  decodeSetShopPrice,
  decodeTaxSummary,
} from './contracts'

export interface GoodsPageArgs {
  resourceId: string
  page: number
  pageSize: number
  /**
   * UI-RMK: строка поиска РМК — подстрока наименования или артикула либо
   * штрихкод целиком (его служба ставит первым). Пусто — весь снимок по порядку.
   */
  search?: string
  /**
   * Точный штрихкод (скан): служба отдаёт из своей базы только карточки, у которых он есть целиком, — без единого поиска; нет совпадения —
   * пустая выдача. С `search` и `pricedOnly` не сочетается.
   */
  barcode?: string
  /** Список цен, а не номенклатура: только карточки с ценой. */
  pricedOnly?: boolean
}

export interface SetGoodPriceArgs {
  resourceId: string
  goodId: string
  /** Цена в рублях, больше нуля, до двух знаков. */
  price: number
}

export interface GoodPricesArgs {
  resourceId: string
  goodId: string
}

export interface GoodPrices {
  goodId: string
  name: string | null
  price: number | null
  shops: readonly { shopId: string; name: string | null; resourceShop: boolean }[]
  resourceShopId: string
}

export interface SetShopPriceArgs {
  resourceId: string
  goodId: string
  shopId: string
  price: number
}

export interface SetShopPriceResult {
  outcome: 'accepted'
  shopId: string
  price: number
  confirmed: boolean
}

export interface SetGoodPriceResult {
  outcome: 'applied' | 'unchanged'
  price: number
}

export interface ProbeSearchArgs {
  resourceId: string
  q: string
  /** С 1: так страницу называет служба поиска. */
  page: number
}

export interface CatalogResourceArgs {
  resourceId: string
}

export interface FreshnessCheckArgs {
  resourceId: string
  /** «Проверить полностью»: перечитать весь кабинет, а не пробу. */
  full?: boolean
}

export interface FiscalCheckArgs {
  resourceId: string
  deviceId: number
  goodIds: readonly string[]
}

/**
 * Снимки кабинета C2. Чтение — из базы службы; «Обновить» — служба читает
 * кабинет ЭТОГО rid (только GET). Обновление каталога не повторяется
 * transport-ом: его исход проверяется перечитыванием снимка.
 */
export const catalogApi = baseApi.enhanceEndpoints({ addTagTypes: AQSI_TAG_TYPES }).injectEndpoints({
  endpoints: (build) => ({
    aqsiGoods: build.query<AqsiGoodsPage, GoodsPageArgs>({
      query: ({ resourceId, page, pageSize, search, barcode, pricedOnly }) => ({
        url: AQSI_URLS.goods(resourceId),
        params: {
          page,
          pageSize,
          ...(barcode !== undefined && barcode.trim() !== '' ? { barcode: barcode.trim() } : {}),
          ...(search !== undefined && search.trim() !== '' ? { search: search.trim() } : {}),
          ...(pricedOnly === true ? { pricedOnly: true } : {}),
        },
        decode: decodeGoodsPage(resourceId),
      }),
      providesTags: (_result, _error, { resourceId, page }) => [
        resourceListTag(AQSI_TAGS.goods, resourceId),
        resourceTag(AQSI_TAGS.goods, resourceId, `page-${page}`),
        resourceScopeTag(resourceId),
      ],
    }),
    /**
     * RP-SE12: поиск вкладки «Каталог AQSI» — в ОТДЕЛЬНОМ индексе единой службы поиска по снимку кабинета (прямого поиска у AQSI нет: его API
     * не ищет по строке). Карточки приходят в порядке службы. Состояние индекса — `aqsiProbeStatus`.
     */
    aqsiProbeSearch: build.query<AqsiGoodsPage, ProbeSearchArgs>({
      query: ({ resourceId, q, page }) => ({
        url: AQSI_URLS.probeSearch(resourceId),
        params: { q: q.trim(), page },
        decode: decodeGoodsPage(resourceId),
      }),
      providesTags: (_result, _error, { resourceId }) => [resourceListTag(AQSI_TAGS.probe, resourceId), resourceScopeTag(resourceId)],
    }),
    aqsiProbeStatus: build.query<AqsiProbeStatus, CatalogResourceArgs>({
      query: ({ resourceId }) => ({ url: AQSI_URLS.probeStatus(resourceId), decode: decodeProbeStatus(resourceId) }),
      providesTags: (_result, _error, { resourceId }) => [resourceListTag(AQSI_TAGS.probeStatus, resourceId), resourceScopeTag(resourceId)],
    }),
    /** «Перестроить индекс»: из снимка в нашей базе, кабинет не перечитывается; исход виден в состоянии индекса. */
    aqsiProbeRebuild: build.mutation<AqsiProbeStatus, CatalogResourceArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_URLS.probeRebuild(resourceId),
        method: 'POST',
        decode: decodeProbeStatus(resourceId),
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.probeStatus, resourceId),
        resourceListTag(AQSI_TAGS.probe, resourceId),
      ],
    }),
    /** Сводка ставки НДС товаров кабинета (вкладка «Настройка» номенклатуры): из снимка в нашей базе, кабинет не трогается. */
    aqsiTaxSummary: build.query<AqsiTaxSummary, CatalogResourceArgs>({
      query: ({ resourceId }) => ({ url: AQSI_URLS.taxSummary(resourceId), decode: decodeTaxSummary(resourceId) }),
      providesTags: (_result, _error, { resourceId }) => [resourceListTag(AQSI_TAGS.taxSummary, resourceId), resourceScopeTag(resourceId)],
    }),
    /** RP-SE13: свежесть каталога кабинета для полосы «Источники». Читает базу службы, кабинет не трогает. */
    aqsiFreshness: build.query<AqsiFreshness, CatalogResourceArgs>({
      query: ({ resourceId }) => ({ url: AQSI_URLS.freshness(resourceId), decode: decodeFreshness(resourceId) }),
      providesTags: (_result, _error, { resourceId }) => [resourceListTag(AQSI_TAGS.freshness, resourceId), resourceScopeTag(resourceId)],
    }),
    /**
     * RP-SE13: «Проверить актуальность» - проверка сейчас, но через тот же single-flight и минимальный промежуток, что и расписание: идёт ли
     * уже проверка или прошла только что - в ответе `status`, запроса в кабинет тогда нет.
     */
    aqsiFreshnessCheck: build.mutation<AqsiFreshnessCheck, FreshnessCheckArgs>({
      query: ({ resourceId, full }) => ({
        url: AQSI_URLS.freshnessCheck(resourceId),
        method: 'POST',
        ...(full === true ? { params: { full: true } } : {}),
        decode: decodeFreshnessCheck(resourceId),
        timeoutMs: 120_000,
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.freshness, resourceId),
        resourceListTag(AQSI_TAGS.goods, resourceId),
        resourceListTag(AQSI_TAGS.probeStatus, resourceId),
      ],
    }),
    /** RP-SE13: сигнал «я в системе»; ответа нет. Ошибка сигнала человеку не показывается. */
    aqsiPresence: build.mutation<null, void>({
      query: () => ({ url: AQSI_URLS.presence(), method: 'POST', decode: () => null }),
    }),
    aqsiDevices: build.query<AqsiDevices, CatalogResourceArgs>({
      query: ({ resourceId }) => ({ url: AQSI_URLS.devices(resourceId), decode: decodeDevices(resourceId) }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.devices, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiCashiers: build.query<AqsiCashiers, CatalogResourceArgs>({
      query: ({ resourceId }) => ({ url: AQSI_URLS.cashiers(resourceId), decode: decodeCashiers(resourceId) }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.cashiers, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiCatalogRefresh: build.mutation<AqsiRefreshResult, CatalogResourceArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_URLS.catalogRefresh(resourceId),
        method: 'POST',
        decode: decodeRefresh(resourceId),
        timeoutMs: 120_000,
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.goods, resourceId),
        resourceListTag(AQSI_TAGS.probeStatus, resourceId),
      ],
    }),
    /**
     * Управление ценами: один PUT без повторов. Служба пишет цену в кабинет (`v2/Goods/prices`), читает карточку и только после
     * подтверждения кладёт цену в снимок; не подтвердила — 503 `price-not-confirmed`, и повторять запись не нужно.
     */
    aqsiSetGoodPrice: build.mutation<SetGoodPriceResult, SetGoodPriceArgs>({
      query: ({ resourceId, goodId, price }) => ({
        url: AQSI_URLS.goodPrice(resourceId, goodId),
        method: 'PUT',
        body: { price },
        decode: decodeSetPrice,
      }),
      invalidatesTags: (_result, _error, { resourceId, goodId }) => [
        resourceListTag(AQSI_TAGS.goods, resourceId),
        resourceTag(AQSI_TAGS.goods, resourceId, `prices-${goodId}`),
      ],
    }),
    /** RP-FB39: цены карточки живьём — цена товара и магазины, к которым он привязан. Только GET к кабинету. */
    aqsiGoodPrices: build.query<GoodPrices, GoodPricesArgs>({
      query: ({ resourceId, goodId }) => ({ url: AQSI_URLS.goodPrices(resourceId, goodId), decode: decodeGoodPrices }),
      providesTags: (_result, _error, { resourceId, goodId }) => [resourceTag(AQSI_TAGS.goods, resourceId, `prices-${goodId}`), resourceScopeTag(resourceId)],
    }),
    /** Цена товара в одном магазине: один PUT без повторов; саму цену магазина кабинет обратно не отдаёт. */
    aqsiSetShopPrice: build.mutation<SetShopPriceResult, SetShopPriceArgs>({
      query: ({ resourceId, goodId, shopId, price }) => ({
        url: AQSI_URLS.goodShopPrice(resourceId, goodId),
        method: 'PUT',
        body: { shopId, price },
        decode: decodeSetShopPrice,
      }),
      invalidatesTags: (_result, _error, { resourceId, goodId }) => [resourceTag(AQSI_TAGS.goods, resourceId, `prices-${goodId}`)],
    }),
    aqsiDevicesRefresh: build.mutation<AqsiRefreshResult, CatalogResourceArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_URLS.devicesRefresh(resourceId),
        method: 'POST',
        decode: decodeRefresh(resourceId),
        timeoutMs: 120_000,
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.devices, resourceId),
        resourceListTag(AQSI_TAGS.cashiers, resourceId),
      ],
    }),
    /** Проверка профиля — чтение базы службы, POST только из-за тела запроса; эффекта нет. */
    aqsiFiscalCheck: build.mutation<AqsiFiscalCheck, FiscalCheckArgs>({
      query: ({ resourceId, deviceId, goodIds }) => ({
        url: AQSI_URLS.fiscalCheck(resourceId),
        method: 'POST',
        body: { deviceId, goodIds },
        decode: decodeFiscalCheck(resourceId),
      }),
    }),
  }),
})

export const {
  aqsiFreshness,
  aqsiTaxSummary,
  aqsiFreshnessCheck,
  aqsiPresence,
  aqsiGoods,
  aqsiProbeSearch,
  aqsiProbeStatus,
  aqsiProbeRebuild,
  aqsiDevices,
  aqsiCashiers,
  aqsiCatalogRefresh,
  aqsiSetGoodPrice,
  aqsiGoodPrices,
  aqsiSetShopPrice,
  aqsiDevicesRefresh,
  aqsiFiscalCheck,
} = catalogApi.endpoints
